export interface MarketingEntry {
  landing_path: string;
  referrer_host: string;
  utm: Partial<
    Record<"source" | "medium" | "campaign" | "content" | "term", string>
  >;
  click_identifiers: { platform: string; type: string; value: string }[];
}

export function hasUnsafeMarketingText(value: string): boolean {
  return Array.from(value).some(
    (character) =>
      character === "<" ||
      character === ">" ||
      character.charCodeAt(0) < 32 ||
      character.charCodeAt(0) === 127,
  );
}

export function parseMarketingEntry(
  href: string,
  referrer: string,
  advertising: boolean,
): MarketingEntry {
  const entry: MarketingEntry = {
    landing_path: "/",
    referrer_host: "",
    utm: {},
    click_identifiers: [],
  };
  try {
    const url = new URL(href);
    entry.landing_path = /^\/[\w\-/]*$/.test(url.pathname)
      ? url.pathname.substring(0, 512)
      : "/";
    const permitted = (key: string, limit: number): string | undefined => {
      const values = url.searchParams.getAll(key);
      const value = values[0];
      return values.length === 1 &&
        value &&
        value.length <= limit &&
        !hasUnsafeMarketingText(value)
        ? value
        : undefined;
    };
    for (const name of [
      "source",
      "medium",
      "campaign",
      "content",
      "term",
    ] as const) {
      const value = permitted(`utm_${name}`, 256);
      if (value) entry.utm[name] = value;
    }
    if (advertising) {
      for (const [platform, type] of [
        ["meta", "fbclid"],
        ["google", "gclid"],
        ["google", "gbraid"],
        ["google", "wbraid"],
        ["tiktok", "ttclid"],
      ]) {
        const value = permitted(type, 2048);
        if (value) entry.click_identifiers.push({ platform, type, value });
      }
    }
    if (referrer) {
      const previous = new URL(referrer);
      if (
        previous.hostname !== url.hostname &&
        ["https:", "http:"].includes(previous.protocol)
      )
        entry.referrer_host = previous.hostname.substring(0, 253);
    }
  } catch {
    /* Invalid optional context is not a checkout error. */
  }
  return entry;
}

export function hasMarketingEvidence(entry: MarketingEntry): boolean {
  return !!(
    Object.keys(entry.utm).length ||
    entry.referrer_host ||
    entry.click_identifiers.length
  );
}
